import LoadingPage from '@/views/LoadingPage.vue'
import { accountCreationGuard } from '@/router/routeGuards.ts'
import getApiInstance from '@/services/Api.ts'
import NetworkIssue from '@/views/Otivo/NetworkIssue.vue'
import Maintenance from '@/views/Otivo/Maintenance.vue'
import NotFound from '@/views/Otivo/NotFound.vue'
import Fsg from '@/views/Otivo/Brand/LegalStuff/Fsg.vue'
import ContactUs from '@/views/Otivo/Brand/About/ContactUs.vue'
import { RouteRecordRaw } from 'vue-router'
import CreateAccount from '@/components/CreateAccount/CreateAccount.vue'
import { createAccount, isAuthenticated, login } from '@/lib/AuthenticatorPlugin.ts'
import UserDetailsForm from '@/components/CreateAccount/Partials/UserDetailsForm.vue'
// import MobileVerificationForm from '@/components/CreateAccount/Partials/MobileVerificationForm.vue'
import CaptureFundMemberId from '@/components/CreateAccount/Partials/CaptureFundMemberId.vue'
import TermsAndConditionsForm from '@/components/CreateAccount/Partials/TermsAndConditionsForm.vue'
import CaptureSuperBalance from '@/components/CreateAccount/Partials/CaptureSuperBalance.vue'
import CaptureSuperAccountNumber from '@/components/CreateAccount/Partials/CaptureSuperAccountNumber.vue'
import CaptureInvestmentOptions from '@/components/CreateAccount/Partials/CaptureInvestmentOptions.vue'
import SelectSuperFund from '@/components/CreateAccount/Partials/SelectSuperFund.vue'
import Paywall from '@/components/CreateAccount/Paywall/Paywall.vue'
import SecurityPage from '@/views/Otivo/Brand/About/Security.vue'
import Media from '@/views/Otivo/Brand/About/Media.vue'
import HowOtivoMakesMoney from '@/views/Otivo/Brand/About/HowOtivoMakesMoney.vue'
import PersonalPricing from '@/views/Otivo/Brand/Solutions/PersonalPricing.vue'
import OnboardingIntroductoryBoostSuperPage from '@/views/Otivo/Brand/SplashScreens/OnboardingIntroductoryBoostSuperPage.vue'
import OnboardingIntroductoryInsurancePage from '@/views/Otivo/Brand/SplashScreens/OnboardingIntroductoryInsurancePage.vue'
import OnboardingIntroductoryOptimizeSuperPage from '@/views/Otivo/Brand/SplashScreens/OnboardingIntroductoryOptimizeSuperPage.vue'
import AdvicePractices from '@/views/Otivo/Brand/Solutions/AdvicePractices.vue'
const SuperFunds = () => import('@/views/Otivo/Brand/Solutions/SuperFunds.vue')
import OnboardingIntroductorySalarySacrificePage from '@/views/Otivo/Brand/SplashScreens/OnboardingIntroductorySalarySacrificePage.vue'
import ExtraContent from '@/views/Otivo/Brand/LegalStuff/ExtraContent.vue'
import ParseJWT from '@/composables/decryptWBGJWT.ts'
import type { ISessionStore } from '@/store/pinia/SessionStore.ts'
import type { IAccountFlagsStore } from '@/store/pinia/AccountFlagsStore.ts'
import type { IUserStore } from '@/store/pinia/UserStore.ts'
import dashboardRoutes from '@/router/features/dashboardRoutes.ts'
import UnsubscribeEmail from '@/components/Profile/EmailSubscription/UnsubscribeEmailPage.vue'
import DeactivateAccount from '@/components/Profile/Deactivate/DeactivateAccount.vue'
import DeactivateSurvey from '@/components/Profile/Deactivate/DeactivateSurvey.vue'
import DeactivateAccountView from '@/components/Profile/Deactivate/DeactivateAccountView.vue'
import DeactivateFarewell from '@/components/Profile/Deactivate/DeactivateFarewell.vue'
import DebtNuggetView from '@/views/Otivo/Brand/SplashScreens/DebtNuggetView.vue'
import GetStarted from '@/components/FreemiumOnboarding/GetStarted.vue'
import SuperFundDisclaimerNew from '@/components/CreateAccount/Partials/SuperFundDisclaimerNew.vue'
import CFSDisclaimer from '@/components/CreateAccount/Partials/CFSDisclaimer.vue'
import { useFreemium } from '@/composables/freemium/useFreemium.ts'
import OtivoRetirementCalculator from '@/components/OtivoRetirementCalculator/OtivoRetirementCalculator.vue'

export default (
  sessionStore: ISessionStore,
  userStore: IUserStore,
  accountFlagsStore: IAccountFlagsStore,
): Array<RouteRecordRaw> => {

  const freemiumRoutes: Array<RouteRecordRaw> = [
    {
      path: '/get-started',
      name: 'get-started',
      component: GetStarted,
    },
    {
      path: '/create-account',
      name: 'create-account',
      component: CreateAccount,
      beforeEnter: (to, from, next) => accountCreationGuard(to, from, next, userStore),
    },
  ]
  const createAccountRoutes: Array<RouteRecordRaw> = [
    {
      path: '/get-started',
      name: 'get-started',
      component: LoadingPage,
      beforeEnter: () => createAccount(),
    },
    {
      path: '/sign-up',
      component: LoadingPage,
      beforeEnter: async (to) => {
        const params = new URLSearchParams(window.location.search)
        // scrape WBG token if it exists
        if (params.has('token')) {
          await ParseJWT(params.get('token') as string, import.meta.env.VITE_WBGK).then((token) => {
            localStorage.setItem('wbg_details', JSON.stringify(token))
            history.pushState({}, '', to as string)
          })
        }
        createAccount()
      },
    },
    {
      path: '/create-account',
      name: 'create-account',
      component: CreateAccount,
      beforeEnter: (to, from, next) => accountCreationGuard(to, from, next, userStore),
      children: [
        {
          path: 'cfs-disclaimer',
          name: 'CFSDisclaimer',
          component: CFSDisclaimer,
        },
        {
          path: 'details',
          name: 'UserDetailsForm',
          component: UserDetailsForm,
        },
        // {
        //   path: 'verify',
        //   name: 'MobileVerification',
        //   component: MobileVerificationForm,
        // },
        {
          path: 'member-details',
          name: 'CaptureFundMemberId',
          component: CaptureFundMemberId,
        },
        {
          path: 'select-fund',
          name: 'SelectSuperFund',
          component: SelectSuperFund,
        },
        {
          path: 'account-number',
          name: 'CaptureSuperAccountNumber',
          component: CaptureSuperAccountNumber,
        },
        {
          path: 'super-balance',
          name: 'CaptureSuperBalance',
          component: CaptureSuperBalance,
        },
        {
          path: 'super-investment-options',
          name: 'CaptureInvestmentOptions',
          component: CaptureInvestmentOptions,
        },
        {
          path: 'subscribe',
          name: 'Paywall',
          component: Paywall,
          beforeEnter: (to, from, next) => {
            const whitelabel = sessionStore.getWhitelabelData
            if (whitelabel.subscription_required) next()
            else next({ name: 'clientDashboard' })
          },
        },
        {
          path: 'terms',
          name: 'TermsAndConditionsForm',
          component: TermsAndConditionsForm,
        },
        {
          path: 'superfund-disclaimer',
          name: 'SuperFundDisclaimer',
          component: SuperFundDisclaimerNew,
        },
      ],
    },
  ]
  const baseRoutes: Array<RouteRecordRaw> = [
    {
      path: '/playground',
      name: 'playground',
      component: OtivoRetirementCalculator,
    },
    {
      path: '/login',
      name: 'login',
      component: LoadingPage,
      beforeEnter: () => login(),
    },
    {
      path: '/login/callback',
      name: 'callback',
      component: LoadingPage,
      // beforeEnter: (to, from, next) => handleLoginCallback(to, from, next),
    },
    {
      path: '/logout',
      name: 'logout',
      component: LoadingPage,
      beforeEnter: async () => {
        sessionStore.logout()
      },
    },

    {
      path: '/deactivate',
      name: 'Deactivate',
      redirect: '/deactivate/account',
      beforeEnter: (to, from, next) => {
        if (!isAuthenticated.value) login() // will redirect to login page && stop execution
        next()
      },
      component: DeactivateAccountView,
      children: [
        {
          path: 'account',
          name: 'DeactivateAccount',
          component: DeactivateAccount,
        },
        {
          path: 'survey',
          name: 'DeactivateSurvey',
          component: DeactivateSurvey,
        },
        {
          path: 'farewell',
          name: 'DeactivateFarewell',
          component: DeactivateFarewell,
        },
      ],
    },
    {
      path: '/impersonate',
      name: 'impersonation',
      component: LoadingPage,
      beforeEnter: (to, from, next) => {
        const api = getApiInstance()
        const token = to.query.token as string
        if (token) {
          api.setImpersonationHeader(token)
          sessionStorage.setItem('impersonationToken', token)
          sessionStore.setImpersonationState(true)
        }
        next({ name: 'dashboard' })
      },
    },
    {
      path: '/network-issue',
      name: 'network-issue',
      component: NetworkIssue,
    },
    {
      path: '/maintenance',
      name: 'maintenance',
      component: Maintenance,
    },
    {
      path: '/fsg',
      name: 'fsg',
      component: Fsg,
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: NotFound,
    },
    {
      path: '/contact-us',
      name: 'contact-us',
      component: ContactUs,
    },
    {
      path: '/security',
      name: 'security',
      component: SecurityPage,
    },
    {
      path: '/media',
      name: 'media',
      component: Media,
    },
    {
      path: '/how-otivo-makes-money',
      name: 'how-otivo-makes-money',
      component: HowOtivoMakesMoney,
    },
    {
      path: '/personal-pricing',
      name: 'personal-pricing',
      meta: {
        type: 'personal',
      },
      beforeEnter: (to, from, next) => {
        const freemiumEnabled = useFreemium().freemiumEnabled
        if (freemiumEnabled.value) {
          next('/')
        } else {
          next()
        }
      },
      component: PersonalPricing,
    },
    {
      path: '/advice-practices',
      name: 'advice-practices',
      meta: {
        type: 'business',
      },
      component: AdvicePractices,
    },
    {
      path: '/super-funds',
      name: 'super-funds',
      meta: {
        type: 'business',
      },
      component: SuperFunds,
    },
    {
      path: '/intro/boost-super',
      name: 'intro-boost-super',
      component: OnboardingIntroductoryBoostSuperPage,
    },
    {
      path: '/intro/pay-debt',
      name: 'intro-pay-debt',
      component: DebtNuggetView,
    },
    {
      path: '/intro/insurance',
      name: 'intro-insurance',
      component: OnboardingIntroductoryInsurancePage,
    },
    {
      path: '/intro/optimize-super',
      name: 'intro-optimize-super',
      component: OnboardingIntroductoryOptimizeSuperPage,
    },
    {
      path: '/intro/salary-sacrifice',
      name: 'intro-salary-sacrifice',
      component: OnboardingIntroductorySalarySacrificePage,
    },
    {
      path: '/content/:content_type',
      name: 'content',
      component: ExtraContent,
    },
    {
      path: '/email/:subscriptionId/:userId',
      name: 'unsubscribe-email',
      component: UnsubscribeEmail,
    },
    {
      path: '/wealth-calculator',
      name: 'wealth-calculator',
      redirect: '/retirement-calculator',
    },
    ...dashboardRoutes(userStore, sessionStore, accountFlagsStore),
  ]

  let onboardRoutes: Array<RouteRecordRaw>
  const freemiumEnabled = useFreemium().freemiumEnabled
  freemiumEnabled.value
    ? (onboardRoutes = freemiumRoutes) // ignore these routes
    : (onboardRoutes = createAccountRoutes)
  return [...baseRoutes, ...onboardRoutes]
}
